import { Link } from "react-router-dom";
import { Heart, MapPin, Bed, Bath, Square, ChevronRight, Facebook, Twitter, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/LanguageContext";

const PropertyCard = ({ property }) => {
  const { t } = useLanguage();
  const isRent = property.listing_type === "rent";
  const priceDisplay = isRent 
    ? `$${property.price.toLocaleString()}/mo` 
    : `$${property.price.toLocaleString()}`;

  const shareUrl = encodeURIComponent(`${window.location.origin}/property/${property.id}`);
  const shareText = encodeURIComponent(`Check out this amazing property: ${property.title} - $${property.price.toLocaleString()}`);

  return (
    <Card 
      className="group rounded-2xl overflow-hidden border-border/50 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1"
      data-testid={`property-card-${property.id}`}
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={property.image_url}
          alt={property.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        <div className="absolute top-4 left-4 flex gap-2">
          <Badge className={`${isRent ? 'bg-accent' : 'bg-primary'} text-white`}>
            {isRent ? t('propertyCard.forRent') : t('propertyCard.forSale')}
          </Badge>
          {property.is_featured && (
            <Badge className="bg-charcoal text-white">{t('propertyCard.featured')}</Badge>
          )}
        </div>

        <div className="absolute top-4 right-4 flex gap-2">
          <button 
            className="w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
            data-testid={`favorite-btn-${property.id}`}
          >
            <Heart className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="absolute bottom-4 right-4">
          <div className="flex items-center gap-2">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <Twitter className="w-4 h-4" />
            </a>
            <a
              href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <p className="text-2xl font-heading font-semibold text-primary">
            {priceDisplay}
          </p>
        </div>

        <h3 className="font-heading text-lg font-medium text-charcoal mb-2 line-clamp-1">
          {property.title}
        </h3>

        <p className="flex items-center gap-1 text-muted-foreground text-sm mb-4">
          <MapPin className="w-4 h-4" />
          {property.address}, {property.city}
        </p>

        <div className="flex items-center gap-4 text-sm text-muted-foreground border-t border-border pt-4">
          <span className="flex items-center gap-1">
            <Bed className="w-4 h-4" /> {property.bedrooms} {t('propertyCard.beds')}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="w-4 h-4" /> {property.bathrooms} {t('propertyCard.baths')}
          </span>
          <span className="flex items-center gap-1">
            <Square className="w-4 h-4" /> {property.sqft} {t('propertyCard.sqft')}
          </span>
        </div>

        <Link to={`/property/${property.id}`}>
          <Button 
            variant="outline" 
            className="w-full mt-4 rounded-full"
            data-testid={`view-details-${property.id}`}
          >
            {t('propertyCard.viewDetails')} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    </Card>
  );
};

export default PropertyCard;
